type SelectController = {
  close: () => void
  sync: () => void
}

const controllers = new Set<SelectController>()
let activeController: SelectController | null = null
let nextSelectId = 0
let watching = false

function closeActive() {
  activeController?.close()
  activeController = null
}

function selectIcon(className: string, pathData: string) {
  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  icon.classList.add(className)
  icon.setAttribute('viewBox', '0 0 24 24')
  icon.setAttribute('aria-hidden', 'true')
  path.setAttribute('d', pathData)
  icon.append(path)
  return icon
}

function enhanceSelect(select: HTMLSelectElement) {
  if (select.dataset.appSelectEnhanced === 'true' || select.multiple || select.dataset.nativeSelect === 'true') return
  select.dataset.appSelectEnhanced = 'true'
  select.tabIndex = -1
  select.classList.add('app-native-select')

  const root = document.createElement('span')
  root.className = 'app-select'
  select.parentNode?.insertBefore(root, select)
  root.append(select)

  const trigger = document.createElement('button')
  const value = document.createElement('span')
  const chevron = selectIcon('app-select-chevron', 'm6 9 6 6 6-6')
  const menu = document.createElement('div')
  const menuId = `app-select-menu-${++nextSelectId}`
  trigger.type = 'button'
  trigger.className = 'app-select-trigger'
  trigger.setAttribute('aria-haspopup', 'listbox')
  trigger.setAttribute('aria-expanded', 'false')
  trigger.setAttribute('aria-controls', menuId)
  menu.id = menuId
  menu.className = 'app-select-menu'
  menu.setAttribute('role', 'listbox')
  menu.hidden = true
  trigger.append(value, chevron)
  root.append(trigger)
  // A native dialog is rendered in the browser's top layer. Keep its select
  // menu in that same layer so it cannot be painted underneath the dialog.
  const menuHost = select.closest('dialog') ?? document.body
  menuHost.append(menu)

  const sync = () => {
    const selected = select.selectedOptions[0] ?? select.options[0]
    value.textContent = selected?.textContent?.trim() || '请选择'
    trigger.disabled = select.disabled
    trigger.setAttribute('aria-label', select.getAttribute('aria-label') || select.closest('label')?.querySelector(':scope > span')?.textContent?.trim() || '选择选项')
    if (select.disabled && !menu.hidden) close()
  }

  const positionMenu = () => {
    const rect = trigger.getBoundingClientRect()
    const viewportGap = 8
    const width = Math.min(Math.max(rect.width, 168), window.innerWidth - viewportGap * 2)
    menu.style.width = `${width}px`
    menu.style.left = `${Math.min(Math.max(viewportGap, rect.left), window.innerWidth - width - viewportGap)}px`
    menu.style.visibility = 'hidden'
    menu.hidden = false
    const height = menu.offsetHeight
    const spaceBelow = window.innerHeight - rect.bottom - viewportGap
    const openAbove = height > spaceBelow && rect.top > spaceBelow
    menu.style.top = `${openAbove ? Math.max(viewportGap, rect.top - height - 6) : Math.min(window.innerHeight - height - viewportGap, rect.bottom + 6)}px`
    menu.dataset.placement = openAbove ? 'top' : 'bottom'
    menu.style.visibility = ''
  }

  const close = () => {
    menu.hidden = true
    trigger.setAttribute('aria-expanded', 'false')
    root.classList.remove('is-open')
    if (activeController === controller) activeController = null
  }

  const open = () => {
    if (select.disabled) return
    closeActive()
    menu.replaceChildren(...Array.from(select.options).map((option) => {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'app-select-option'
      button.setAttribute('role', 'option')
      button.setAttribute('aria-selected', String(option.selected))
      button.disabled = option.disabled
      button.dataset.value = option.value
      const label = document.createElement('span')
      label.textContent = option.textContent?.trim() || option.value
      button.append(label)
      if (option.selected) button.append(selectIcon('app-select-check', 'm5 12 4 4L19 6'))
      button.addEventListener('click', () => {
        select.value = option.value
        select.dispatchEvent(new Event('input', { bubbles: true }))
        select.dispatchEvent(new Event('change', { bubbles: true }))
        close()
        trigger.focus()
      })
      return button
    }))
    activeController = controller
    root.classList.add('is-open')
    trigger.setAttribute('aria-expanded', 'true')
    positionMenu()
    menu.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
  }

  const controller: SelectController = { close, sync }
  controllers.add(controller)
  trigger.addEventListener('click', () => menu.hidden ? open() : close())
  trigger.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (menu.hidden) open()
    } else if (event.key === 'Escape') {
      close()
    }
  })
  select.addEventListener('change', () => { sync(); close() })
  new MutationObserver(sync).observe(select, { attributes: true, childList: true, subtree: true, characterData: true })
  sync()
}

function enhanceWithin(root: ParentNode) {
  if (root instanceof HTMLSelectElement) enhanceSelect(root)
  root.querySelectorAll?.('select').forEach((select) => enhanceSelect(select as HTMLSelectElement))
}

export function enhanceDocumentSelects() {
  enhanceWithin(document)
  if (watching) return
  watching = true
  new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach((node) => {
    if (node instanceof HTMLElement) enhanceWithin(node)
  }))).observe(document.body, { childList: true, subtree: true })
  document.addEventListener('pointerdown', (event) => {
    const target = event.target as Node
    if (activeController && !document.querySelector('.app-select.is-open')?.contains(target) && !document.querySelector('.app-select-menu:not([hidden])')?.contains(target)) closeActive()
    controllers.forEach((controller) => controller.sync())
  })
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeActive() })
  window.addEventListener('resize', closeActive)
  window.addEventListener('scroll', (event) => {
    const menu = document.querySelector<HTMLElement>('.app-select-menu:not([hidden])')
    const target = event.target
    // Scrolling inside the open menu is an intentional touch gesture. Only
    // close when the page or another scroll container moves underneath it.
    if (!menu || !(target instanceof Node) || !menu.contains(target)) closeActive()
  }, true)
}
